import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDeals } from '../../context/DataContext';
import { useInvoices } from '../../context/InvoicesContext';
import { fmtMoney } from '../../lib/format';
import { INVOICE_COLUMNS, dealInvoiceColumn, dealTalentIds } from '../../lib/deals';
import { ExternalLink, MessageSquare } from 'lucide-react';

const todayISO = () => new Date().toISOString().slice(0, 10);

const InvoiceCard = ({ deal, brandMap, talentMap, canEdit, onNavigate, setDragging, dragging }) => {
  const brand = brandMap[deal.brand_id];
  const talentNames = dealTalentIds(deal).map((id) => talentMap?.[id]?.name).filter(Boolean).join(', ');
  const notesCount = (deal.invoice_notes || []).length;
  return (
    <div
      draggable={canEdit}
      onDragStart={() => canEdit && setDragging(deal.id)}
      onDragEnd={() => setDragging(null)}
      onClick={() => onNavigate(`/deals/${deal.id}`)}
      style={{ opacity: dragging === deal.id ? 0.5 : 1 }}
      className={`rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm hover:shadow-md hover:border-pink-300 transition ${canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
      data-testid={`invoice-kanban-card-${deal.id}`}
    >
      <div className="flex items-start justify-between gap-1.5">
        <span className="text-sm font-medium text-slate-800 leading-snug line-clamp-2">{deal.title}</span>
        <ExternalLink className="w-3 h-3 text-slate-300 shrink-0 mt-0.5" />
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5 truncate">{brand?.name || '—'}{talentNames ? ` × ${talentNames}` : ''}</div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-sm font-semibold text-slate-900">{fmtMoney(Number(deal.value) || 0, deal.currency)}</span>
        {notesCount > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-pink-600"><MessageSquare className="w-3 h-3" />{notesCount}</span>
        )}
      </div>
    </div>
  );
};

const Column = ({ col, deals, brandMap, talentMap, canEdit, onNavigate, onDropCard, dragging, setDragging }) => {
  const [over, setOver] = useState(false);
  const total = deals.reduce((s, d) => s + (Number(d.value) || 0), 0);
  return (
    <div
      className={`flex-1 min-w-[200px] flex flex-col rounded-xl border overflow-hidden transition-all bg-white ${over ? 'border-pink-400 shadow-lg shadow-pink-100' : 'border-slate-200'}`}
      onDragOver={(e) => { if (canEdit) { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDropCard(col.id); }}
      data-testid={`invoice-kanban-column-${col.id}`}
    >
      <div className="px-3 py-2.5 flex items-center justify-between border-b" style={{ borderColor: col.color + '33', background: col.color + '12' }}>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: col.color }} />
          <h3 className="text-sm font-semibold lowercase" style={{ color: col.color }}>{col.label}</h3>
          <span className="text-xs font-semibold inline-flex items-center justify-center min-w-[20px] h-5 rounded-full px-1.5 text-white" style={{ background: col.color }}>{deals.length}</span>
        </div>
      </div>
      <div className="px-3 py-1.5 text-[11px] font-semibold border-b border-slate-100 text-slate-500">{fmtMoney(total)}</div>
      <div className="flex-1 p-2 space-y-1.5 overflow-y-auto bg-slate-50/40" style={{ minHeight: 220 }}>
        {deals.map((deal) => (
          <InvoiceCard
            key={deal.id}
            deal={deal}
            brandMap={brandMap}
            talentMap={talentMap}
            canEdit={canEdit}
            onNavigate={onNavigate}
            dragging={dragging}
            setDragging={setDragging}
          />
        ))}
        {deals.length === 0 && (
          <div className="text-center text-xs text-slate-400 py-6 border border-dashed border-slate-200 rounded-lg">{canEdit ? 'drop deals here' : 'nothing here'}</div>
        )}
      </div>
    </div>
  );
};

const InvoicingKanban = ({ q, talentId = 'all', canEdit = true }) => {
  const nav = useNavigate();
  const { deals, brandMap, talentMap, updateDeal } = useDeals();
  const { invoices } = useInvoices();
  const [dragging, setDragging] = useState(null);

  const visible = useMemo(() => {
    return deals.filter((d) => {
      if ((Number(d.value) || 0) <= 0) return false;
      if (!d.ready_to_invoice) return false;
      if (talentId && talentId !== 'all' && !dealTalentIds(d).includes(talentId)) return false;
      if (q?.trim()) {
        const s = q.toLowerCase();
        const b = brandMap[d.brand_id];
        const tNames = dealTalentIds(d).map((id) => talentMap?.[id]?.name || '').join(' ');
        if (!`${d.title || ''} ${b?.name || ''} ${tNames}`.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [deals, brandMap, talentMap, q, talentId]);

  const byColumn = useMemo(() => {
    const map = Object.fromEntries(INVOICE_COLUMNS.map((c) => [c.id, []]));
    visible.forEach((d) => {
      const dealInvs = invoices.filter((i) => i.deal_id === d.id);
      const col = dealInvoiceColumn(d, dealInvs);
      (map[col] || map.to_invoice).push(d);
    });
    return map;
  }, [visible, invoices]);

  const moveTo = (colId) => {
    if (!canEdit || !dragging) return;
    const deal = deals.find((d) => d.id === dragging);
    setDragging(null);
    if (!deal) return;
    const dealInvs = invoices.filter((i) => i.deal_id === deal.id);
    if (dealInvoiceColumn(deal, dealInvs) === colId) return;
    let patch;
    if (colId === 'paid_out') {
      patch = { paid_out_at: deal.paid_out_at || todayISO() };
    } else if (colId === 'invoiced') {
      patch = { paid_out_at: '', invoice_status: 'invoiced', invoiced_at: deal.invoiced_at || todayISO() };
    } else if (colId === 'overdue') {
      patch = { paid_out_at: '', invoice_status: 'overdue' };
    } else if (colId === 'awaiting_po') {
      patch = { paid_out_at: '', invoice_status: 'awaiting_po' };
    } else {
      patch = { paid_out_at: '', invoice_status: 'to_invoice' };
    }
    updateDeal(deal.id, patch);
  };

  return (
    <div className="overflow-x-auto pb-4" data-testid="invoicing-kanban">
      <div className="flex gap-3 w-full">
        {INVOICE_COLUMNS.map((col) => (
          <Column
            key={col.id}
            col={col}
            deals={byColumn[col.id] || []}
            brandMap={brandMap}
            talentMap={talentMap}
            canEdit={canEdit}
            onNavigate={(url) => nav(url)}
            onDropCard={moveTo}
            dragging={dragging}
            setDragging={setDragging}
          />
        ))}
      </div>
    </div>
  );
};

export default InvoicingKanban;
